import React, { useState, useEffect, useRef, useMemo } from "react";
import axios from "axios";
import { QRCodeCanvas } from "qrcode.react";
import { useNavigate } from "react-router-dom";

import { translate } from "../../../hooks/translate";
import { useAuth } from "../../../services/useAuth";
import { hasPageWriteAccess } from "../../../config/roles";
import { withAuth } from "../../../components/HOC/withAuth";
import { getStatusAppearance } from "../../../config/statusColors";
import {
  formatDateTimeTrInAppTz,
  formatInstantDateShortTr,
} from "../../../utils/appTime";

import AppPage from "../../../components/CustomUI/AppPage";
import Container from "../../../components/CustomUI/Container";
import ContainerItem from "../../../components/CustomUI/Container/ContainerItem";
import { Table, Tr, Td } from "../../../components/BaseUI/Table";
import { ToastContainer, Toast } from "../../../components/CustomUI/Toast";
import Button from "../../../components/BaseUI/Button";
import Label from "../../../components/BaseUI/Label";
import Input from "../../../components/BaseUI/Input";
import {
  Dropdown,
  DropdownContent,
  DropdownItem,
} from "../../../components/BaseUI/Dropdown";
import SidebarContainer from "../../../components/CustomUI/SidebarContainer";
import {
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
} from "../../../components/BaseUI/Modal";
import ButtonGroup from "../../../components/BaseUI/ButtonGroup";
import {
  FiPlus,
  FiEdit3,
  FiTrash2,
  FiArrowLeft,
  FiEye,
  FiTruck,
  FiExternalLink,
} from "react-icons/fi";
import { IoMdBarcode } from "react-icons/io";

import { useTitle } from "../../../hooks/useTitle";
import { useColumnVisibility } from "../../../hooks/useColumnVisibility";
import ListViewOptionsPanel from "../shared/ListViewOptionsPanel";
import {
  buildRadioFilterSection,
  countTruthy,
} from "../shared/buildListFilterSections";
import Pagination from "../../../components/BaseUI/Pagination";

const API_URL = process.env.REACT_APP_BACKEND_URL || "http://localhost:3100";

const Devices = () => {
  const navigate = useNavigate();
  const [devices, setDevices] = useState([]);
  const [toasts, setToasts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [modelsLoading, setModelsLoading] = useState(true);
  const [selectedModel, setSelectedModel] = useState(null);
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 10,
    total: 0,
    totalPages: 0,
    hasNextPage: false,
    hasPrevPage: false,
  });
  const [models, setModels] = useState([]);
  const [modelFilter, setModelFilter] = useState(null);
  const [statusFilter, setStatusFilter] = useState(null);
  const [sidebarOpened, setSidebarOpened] = useState(false);
  const [createLoading, setCreateLoading] = useState(false);
  const [editingDevice, setEditingDevice] = useState(null);
  const [serialNumbers, setSerialNumbers] = useState([]);
  const [showSerialPicker, setShowSerialPicker] = useState(false);
  const [confirmModalOpen, setConfirmModalOpen] = useState(false);
  const [deviceToDelete, setDeviceToDelete] = useState(null);
  const [qrModalOpen, setQrModalOpen] = useState(false);
  const [selectedDevice, setSelectedDevice] = useState(null);
  const [barcodeValue, setBarcodeValue] = useState(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [sort, setSort] = useState({ sortBy: "serialNumber", sortOrder: "desc" });
  const [manualQrModalOpen, setManualQrModalOpen] = useState(false);
  const [manualSerialNumber, setManualSerialNumber] = useState("");
  const [manualBarcodeValue, setManualBarcodeValue] = useState(null);
  const [deliveriesModalOpen, setDeliveriesModalOpen] = useState(false);
  const [deliveriesLoading, setDeliveriesLoading] = useState(false);
  const [deliveriesForDevice, setDeliveriesForDevice] = useState([]);
  const [selectedDeviceForDeliveries, setSelectedDeviceForDeliveries] =
    useState(null);
  const [deviceData, setDeviceData] = useState({
    modelId: "",
    selectedModel: null,
    count: "1",
    serialNumber: "",
    status: "production",
    notes: "",
  });
  const [showSingleCreateModal, setShowSingleCreateModal] = useState(false);
  const [singleDeviceData, setSingleDeviceData] = useState({
    modelId: "",
    selectedModel: null,
    serialNumber: "",
    month: new Date().getMonth() + 1,
    year: new Date().getFullYear() % 100,
    sequence: "",
    status: "production",
    notes: "",
  });

  const { getToken, getUser } = useAuth();
  const user = getUser();
  const canWrite =
    !!user &&
    hasPageWriteAccess(
      user.role,
      "/products",
      user.pages || [],
      !!user.fullAccess,
    );

  useTitle("Rovion MES - Tüm Ürünler");

  const modelColumns = [
    {
      name: "Model Adı",
      dataType: "text",
      id: 0,
      customSorter: (a, b) =>
        String(a.name || "").localeCompare(String(b.name || "")),
    },
    {
      name: "Model Kodu",
      dataType: "text",
      id: 1,
      customSorter: (a, b) =>
        String(a.code || "").localeCompare(String(b.code || "")),
    },
    {
      name: "Ürün Sayısı",
      dataType: "number",
      id: 2,
      customSorter: (a, b) => (a.deviceCount || 0) - (b.deviceCount || 0),
    },
    { name: "İşlemler", id: 3 },
  ];

  const deviceColumns = [
    { name: "Model", dataType: "text", id: 1, sortable: false },
    { name: "Seri Numarası", dataType: "text", id: 0, sortKey: "serialNumber" },
    {
      name: "Durum",
      dataType: "text",
      id: 2,
      sortKey: "status",
      customSorter: (a, b) =>
        String(a.status || "").localeCompare(String(b.status || "")),
    },
    {
      name: "Oluşturulma",
      dataType: "date",
      id: 3,
      sortKey: "createdAt",
      customSorter: (a, b) => {
        const ta = new Date(a?.createdAt).getTime();
        const tb = new Date(b?.createdAt).getTime();
        return (Number.isFinite(ta) ? ta : 0) - (Number.isFinite(tb) ? tb : 0);
      },
    },
    { name: translate("UI.ACTIONS"), id: 4, sortable: false },
  ];

  const {
    visibleColumns: visibleModelColumns,
    toggleColumnVisibility: toggleModelColumn,
    resetToDefault: resetModelColumns,
  } = useColumnVisibility("products-models-list", modelColumns);

  const {
    visibleColumns: visibleDeviceColumns,
    toggleColumnVisibility: toggleDeviceColumn,
    resetToDefault: resetDeviceColumns,
  } = useColumnVisibility("products-devices-list", deviceColumns);

  const breadcrumbs = [{ label: "Rovion", url: "" }, { label: "Tüm Ürünler" }];

  useEffect(() => {
    fetchDeviceModels();
  }, []);

  useEffect(() => {
    if (selectedModel) {
      fetchDevices();
    }
  }, [
    pagination.page,
    pagination.limit,
    selectedModel,
    statusFilter,
    searchQuery,
    sort.sortBy,
    sort.sortOrder,
  ]);

  useEffect(() => {
    if (sidebarOpened) {
      fetchDeviceModels();
      if (editingDevice) {
        fetchSerialNumbers();
      }
    }
  }, [sidebarOpened, editingDevice]);

  const fetchDevices = async () => {
    if (!selectedModel) return;

    try {
      setLoading(true);
      let url = `${API_URL}/api/devices?page=${pagination.page}&limit=${pagination.limit}&modelId=${selectedModel._id}`;

      if (statusFilter) {
        url += `&status=${statusFilter}`;
      }

      if (searchQuery.trim()) {
        url += `&search=${encodeURIComponent(searchQuery.trim())}`;
      }
      url += `&sortBy=${sort.sortBy}&sortOrder=${sort.sortOrder}`;

      const response = await axios.get(url, {
        headers: {
          Authorization: `Bearer ${getToken()}`,
        },
      });
      console.log(response.data);

      setDevices(response.data.devices || []);
      if (response.data.pagination) {
        setPagination((prev) => ({
          ...prev,
          page: response.data.pagination.page || prev.page,
          limit: response.data.pagination.limit || prev.limit,
          total: response.data.pagination.total || 0,
          totalPages: response.data.pagination.totalPages || 0,
          hasNextPage: response.data.pagination.hasNextPage || false,
          hasPrevPage: response.data.pagination.hasPrevPage || false,
        }));
      }
    } catch (error) {
      console.error("Ürünler getirilemedi:", error);
      setToasts([
        {
          description: "Ürünler yüklenirken bir hata oluştu.",
          id: Date.now(),
          title: "Hata!",
          appearance: "danger",
        },
      ]);
    } finally {
      setLoading(false);
    }
  };

  const fetchDeviceModels = async () => {
    try {
      setModelsLoading(true);
      const response = await axios.get(`${API_URL}/api/device-models`, {
        headers: {
          Authorization: `Bearer ${getToken()}`,
        },
      });
      const activeModels = response.data.filter(
        (model) => model.status === "active",
      );

      // Fetch device counts for each model
      const modelsWithCounts = await Promise.all(
        activeModels.map(async (model) => {
          try {
            const devicesResponse = await axios.get(
              `${API_URL}/api/devices?modelId=${model._id}&limit=1`,
              {
                headers: {
                  Authorization: `Bearer ${getToken()}`,
                },
              },
            );
            return {
              ...model,
              deviceCount: devicesResponse.data.pagination?.total || 0,
            };
          } catch (error) {
            return { ...model, deviceCount: 0 };
          }
        }),
      );

      const splitAlphaNum = (s) => {
        const str = (s ?? "").toString();
        if (!str) return [];
        return str.match(/[A-Za-z]+|\\d+|[^A-Za-z\\d]+/g) || [];
      };

      // A -> Z, compare char-by-char, letters come before numbers
      const compareCharByCharAZ = (a, b) => {
        const ax = splitAlphaNum(a);
        const bx = splitAlphaNum(b);
        const n = Math.max(ax.length, bx.length);

        for (let i = 0; i < n; i++) {
          const as = ax[i] ?? "";
          const bs = bx[i] ?? "";
          if (!as && !bs) return 0;
          if (!as) return -1;
          if (!bs) return 1;

          const aIsDigits = /^\\d+$/.test(as);
          const bIsDigits = /^\\d+$/.test(bs);
          const aIsLetters = /^[A-Za-z]+$/.test(as);
          const bIsLetters = /^[A-Za-z]+$/.test(bs);

          // Type precedence (independent of Z->A): letters first, then numbers, then others
          const typeRank = (isLetters, isDigits) =>
            isLetters ? 0 : isDigits ? 1 : 2;
          const ar = typeRank(aIsLetters, aIsDigits);
          const br = typeRank(bIsLetters, bIsDigits);
          if (ar !== br) return ar - br;

          // Same type
          if (aIsDigits && bIsDigits) {
            // numeric compare (ascending), then length, then string
            const an = Number(as);
            const bn = Number(bs);
            if (an !== bn) return an - bn;
            if (as.length !== bs.length) return as.length - bs.length;
            if (as !== bs)
              return as.localeCompare(bs, "en", { sensitivity: "base" });
            continue;
          }

          // letters/others: locale compare ascending
          const cmp = as.localeCompare(bs, "en", { sensitivity: "base" });
          if (cmp !== 0) return cmp;
        }
        return 0;
      };

      const sorted = [...modelsWithCounts].sort((a, b) => {
        const byCode = compareCharByCharAZ(a?.code || "", b?.code || "");
        if (byCode !== 0) return byCode;
        return compareCharByCharAZ(a?.name || "", b?.name || "");
      });

      setModels(sorted);
    } catch (error) {
      console.error("Modeller getirilemedi:", error);
      setToasts([
        {
          description: "Modeller yüklenirken bir hata oluştu.",
          id: Date.now(),
          title: "Hata!",
          appearance: "danger",
        },
      ]);
    } finally {
      setModelsLoading(false);
    }
  };

  const fetchSerialNumbers = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/serial-numbers`, {
        headers: {
          Authorization: `Bearer ${getToken()}`,
        },
      });
      const availableSerials = Array.isArray(response.data)
        ? response.data.filter((sn) => sn.status === "available")
        : [];
      // Include current device's serial number if editing
      if (editingDevice) {
        const exists = availableSerials.some(
          (sn) => sn.serialNumber === editingDevice.serialNumber,
        );
        if (!exists) {
          availableSerials.push({
            _id: "current",
            serialNumber: editingDevice.serialNumber,
            status: "assigned",
          });
        }
      }
      setSerialNumbers(availableSerials);
    } catch (error) {
      console.error("Seri numaraları getirilemedi:", error);
    }
  };

  const formatDateTime = (dateString) => {
    if (!dateString) return "-";
    return formatDateTimeTrInAppTz(dateString);
  };

  const deliveryModalStyles = {
    summary: {
      border: "1px solid rgba(0,0,0,0.08)",
      borderRadius: 10,
      padding: 12,
      background: "#fff",
      display: "grid",
      gridTemplateColumns: "repeat(2, minmax(0, 1fr))",
      gap: 10,
      marginBottom: 12,
    },
    summaryItem: { display: "flex", flexDirection: "column", gap: 4 },
    summaryLabel: { fontSize: 12, color: "rgba(0,0,0,0.55)", fontWeight: 600 },
    summaryValue: { fontSize: 13, color: "rgba(0,0,0,0.85)" },
    list: { display: "flex", flexDirection: "column", gap: 10 },
    card: {
      border: "1px solid rgba(0,0,0,0.08)",
      borderRadius: 10,
      padding: 12,
      background: "#fff",
    },
    cardTop: {
      display: "flex",
      alignItems: "flex-start",
      justifyContent: "space-between",
      gap: 12,
    },
    grid: {
      display: "grid",
      gridTemplateColumns: "repeat(2, minmax(0, 1fr))",
      gap: 10,
      width: "100%",
    },
    field: { display: "flex", gap: 6, minWidth: 0 },
    label: { fontSize: 12, color: "rgba(0,0,0,0.55)", fontWeight: 600 },
    value: { fontSize: 13, color: "rgba(0,0,0,0.85)", overflow: "hidden" },
    full: { gridColumn: "1 / -1" },
    actions: { flexShrink: 0, display: "flex", justifyContent: "flex-end" },
  };

  const getDeliveryTargetLabel = (delivery) => {
    return (
      delivery?.toCompanyId?.name ||
      delivery?.toDealerId?.name ||
      delivery?.saleId?.companyId?.name ||
      "-"
    );
  };

  const handleOpenDeliveriesModal = async (device) => {
    try {
      setSelectedDeviceForDeliveries(device);
      setDeliveriesModalOpen(true);
      setDeliveriesLoading(true);
      setDeliveriesForDevice([]);

      const response = await axios.get(`${API_URL}/api/deliveries`, {
        headers: { Authorization: `Bearer ${getToken()}` },
      });

      const allDeliveries = response.data?.deliveries || response.data || [];
      const deviceId = device?._id;

      const matches = (Array.isArray(allDeliveries) ? allDeliveries : [])
        .filter((d) => {
          const devicesArr = d?.devices || [];
          return (devicesArr || []).some((dev) => {
            const id = dev?._id || dev?.id || dev;
            return String(id) === String(deviceId);
          });
        })
        .sort((a, b) => {
          const ta = new Date(
            a?.transferDate || a?.deliveryDate || a?.createdAt,
          ).getTime();
          const tb = new Date(
            b?.transferDate || b?.deliveryDate || b?.createdAt,
          ).getTime();
          return tb - ta;
        });

      setDeliveriesForDevice(matches);
    } catch (error) {
      console.error("Teslimatlar getirilemedi:", error);
      setToasts((prev) => [
        ...(prev || []),
        {
          description: "Teslimat bilgileri yüklenirken bir hata oluştu.",
          id: Date.now(),
          title: "Hata!",
          appearance: "danger",
        },
      ]);
      setDeliveriesForDevice([]);
    } finally {
      setDeliveriesLoading(false);
    }
  };

  const getStatusLabel = (status) => {
    const statusLabels = {
      pending: "Beklemede",
      active: "Aktif",
      inactive: "Pasif",
      sold: "Satıldı",
      defective: "Arızalı",
      maintenance: "Bakımda",
      stock: "Stok",
      assembly: "Montaj",
      packaging: "Paketleme",
      delivered: "Teslim Edildi",
    };
    return statusLabels[status] || status;
  };

  const deviceFilterSections = useMemo(
    () => [
      buildRadioFilterSection({
        id: "status",
        title: "Durum Filtresi",
        value: statusFilter ?? "",
        onChange: (v) => {
          setStatusFilter(v ? String(v) : null);
          setPagination((prev) => ({ ...prev, page: 1 }));
        },
        options: [
          { value: "", label: "Tüm Durumlar" },
          ...["stock", "assembly", "packaging", "delivered", "defective"].map(
            (status) => ({
              value: status,
              label: getStatusLabel(status),
            }),
          ),
        ],
      }),
    ],
    [statusFilter],
  );

  const deviceActiveFilterCount = useMemo(
    () => countTruthy([statusFilter]),
    [statusFilter],
  );

  const removeToast = (index) => {
    const values = toasts;
    values.splice(index, 1);
    setToasts([...values]);
  };

  const handleModelClick = (model) => {
    setSelectedModel(model);
    setPagination((prev) => ({ ...prev, page: 1 }));
  };

  const handleBackToModels = () => {
    setSelectedModel(null);
    setDevices([]);
    setPagination((prev) => ({ ...prev, page: 1 }));
  };

  const handleCreateModalOpen = () => {
    setEditingDevice(null);
    setDeviceData({
      modelId: "",
      selectedModel: null,
      count: "1",
      serialNumber: "",
      status: "assembly",
      notes: "",
    });
    setSidebarOpened(true);
  };

  const closeSidebar = () => {
    setSidebarOpened(false);
    setEditingDevice(null);
    setDeviceData({
      modelId: "",
      selectedModel: null,
      count: "1",
      serialNumber: "",
      status: "assembly",
      notes: "",
    });
  };

  const handleEdit = (device) => {
    setEditingDevice(device);
    const selectedModel = models.find(
      (m) => m._id === device.modelId?._id || m._id === device.modelId,
    );
    setDeviceData({
      modelId: device.modelId?._id || device.modelId || "",
      selectedModel: selectedModel || null,
      count: "1",
      serialNumber: device.serialNumber || "",
      status: device.status || "assembly",
      notes: "",
    });
    setSidebarOpened(true);
  };

  const handleDelete = async (deviceId) => {
    setDeviceToDelete(deviceId);
    setConfirmModalOpen(true);
  };

  const handleGenerateQR = async (device) => {
    try {
      setSelectedDevice(device);
      setQrModalOpen(true);
      setBarcodeValue(null);

      const response = await axios.get(
        `${API_URL}/api/serial-numbers/by-serial`,
        {
          params: {
            serialNumber: device.serialNumber,
          },
          headers: {
            Authorization: `Bearer ${getToken()}`,
          },
        },
      );

      if (response.data.success && response.data.serialNumber?.serialNumber) {
        setBarcodeValue(response.data.serialNumber.serialNumber.toLowerCase());
      } else {
        setToasts([
          {
            description: "Barkod bulunamadı.",
            id: Date.now(),
            title: "Hata!",
            appearance: "danger",
          },
        ]);
      }
    } catch (error) {
      console.error("Barkod getirme hatası:", error);
      setToasts([
        {
          description:
            error.response?.data?.message ||
            "Barkod yüklenirken bir hata oluştu.",
          id: Date.now(),
          title: "Hata!",
          appearance: "danger",
        },
      ]);
    }
  };

  // QR codes are rendered directly in JSX (no JsBarcode)

  const handleGenerateQRFromSerial = async () => {
    if (!manualSerialNumber.trim()) {
      setToasts([
        {
          description: "Lütfen bir seri numarası girin.",
          id: Date.now(),
          title: "Uyarı!",
          appearance: "warning",
        },
      ]);
      return;
    }

    try {
      setManualBarcodeValue(null);

      const response = await axios.get(
        `${API_URL}/api/serial-numbers/by-serial`,
        {
          params: {
            serialNumber: manualSerialNumber.trim(),
          },
          headers: {
            Authorization: `Bearer ${getToken()}`,
          },
        },
      );

      if (response.data.success && response.data.serialNumber?.serialNumber) {
        setManualBarcodeValue(
          response.data.serialNumber.serialNumber.toLowerCase(),
        );
      } else {
        // If not found in database, use the entered serial number directly
        setManualBarcodeValue(manualSerialNumber.trim().toLowerCase());
      }
    } catch (error) {
      // If not found, use the entered serial number directly
      console.log(
        "Seri numarası bulunamadı, girilen değer kullanılıyor:",
        error,
      );
      setManualBarcodeValue(manualSerialNumber.trim().toLowerCase());
    }
  };

  const confirmDelete = async () => {
    if (!deviceToDelete) return;

    try {
      await axios.delete(`${API_URL}/api/devices/${deviceToDelete}`, {
        headers: {
          Authorization: `Bearer ${getToken()}`,
        },
      });

      setToasts([
        {
          description: "Ürün başarıyla silindi.",
          id: Date.now(),
          title: "Başarılı!",
          appearance: "success",
        },
      ]);

      fetchDevices();
      setConfirmModalOpen(false);
      setDeviceToDelete(null);
    } catch (error) {
      console.error("Ürün silme hatası:", error);
      setToasts([
        {
          description: error.response?.data?.error || "Ürün silinemedi.",
          id: Date.now(),
          title: "Hata!",
          appearance: "danger",
        },
      ]);
      setConfirmModalOpen(false);
      setDeviceToDelete(null);
    }
  };

  const handleModelSelect = (model) => {
    setDeviceData((prev) => ({
      ...prev,
      selectedModel: model,
      modelId: model._id,
    }));
  };

  const validateCreateForm = () => {
    const errors = [];

    if (!deviceData.modelId) {
      errors.push("Model seçimi zorunludur");
    }

    const count = parseInt(deviceData.count) || 0;
    if (count < 1 || count > 9999) {
      errors.push("Adet 1 ile 9999 arasında olmalıdır");
    }

    return errors;
  };

  const handleSingleCreateSubmit = async () => {
    try {
      setCreateLoading(true);

      if (!singleDeviceData.modelId || !singleDeviceData.selectedModel) {
        setToasts([
          {
            description: "Model zorunludur",
            id: Date.now(),
            title: "Hata!",
            appearance: "danger",
          },
        ]);
        return;
      }

      if (!singleDeviceData.sequence) {
        setToasts([
          {
            description: "Sıra numarası zorunludur",
            id: Date.now(),
            title: "Hata!",
            appearance: "danger",
          },
        ]);
        return;
      }

      const monthYear = `${singleDeviceData.month
        .toString()
        .padStart(2, "0")}${singleDeviceData.year.toString().padStart(2, "0")}`;
      const sequence = singleDeviceData.sequence.toString().padStart(4, "0");
      const serialNumber = `${singleDeviceData.selectedModel.code}/${monthYear}/${sequence}`;

      await axios.post(
        `${API_URL}/api/devices`,
        {
          serialNumber: serialNumber,
          modelId: singleDeviceData.modelId,
          status: "assembly",
        },
        {
          headers: {
            Authorization: `Bearer ${getToken()}`,
          },
        },
      );

      setToasts([
        {
          description: "Ürün başarıyla oluşturuldu.",
          id: Date.now(),
          title: "Başarılı!",
          appearance: "success",
        },
      ]);

      setShowSingleCreateModal(false);
      const now = new Date();
      setSingleDeviceData({
        modelId: "",
        selectedModel: null,
        serialNumber: "",
        month: now.getMonth() + 1,
        year: now.getFullYear() % 100,
        sequence: "",
        status: "assembly",
        notes: "",
      });
      fetchDevices();
    } catch (error) {
      console.error("Ürün oluşturma hatası:", error);
      setToasts([
        {
          description:
            error.response?.data?.error ||
            error.response?.data?.message ||
            "Ürün oluşturulurken bir hata oluştu.",
          id: Date.now(),
          title: "Hata!",
          appearance: "danger",
        },
      ]);
    } finally {
      setCreateLoading(false);
    }
  };

  const handleCreateSubmit = async () => {
    try {
      setCreateLoading(true);

      if (editingDevice) {
        // Edit mode
        if (!deviceData.serialNumber || !deviceData.modelId) {
          setToasts([
            {
              description: "Seri numarası ve model zorunludur",
              id: Date.now(),
              title: "Hata!",
              appearance: "danger",
            },
          ]);
          return;
        }

        await axios.put(
          `${API_URL}/api/devices/${editingDevice._id}`,
          {
            serialNumber: deviceData.serialNumber,
            modelId: deviceData.modelId,
            status: deviceData.status,
            notes: deviceData.notes,
          },
          {
            headers: {
              Authorization: `Bearer ${getToken()}`,
            },
          },
        );

        setToasts([
          {
            description: "Ürün başarıyla güncellendi.",
            id: Date.now(),
            title: "Başarılı!",
            appearance: "success",
          },
        ]);

        closeSidebar();
        fetchDevices();
      } else {
        // Create mode
        const errors = validateCreateForm();
        if (errors.length > 0) {
          setToasts([
            {
              description: errors.join("\n"),
              id: Date.now(),
              title: "Hata!",
              appearance: "danger",
            },
          ]);
          return;
        }

        const count = parseInt(deviceData.count) || 1;
        const response = await axios.post(
          `${API_URL}/api/devices/bulk`,
          {
            modelId: deviceData.modelId,
            count: count,
          },
          {
            headers: {
              Authorization: `Bearer ${getToken()}`,
            },
          },
        );

        const successMessage =
          response.data.errors > 0
            ? `${response.data.created} ürün oluşturuldu, ${response.data.errors} hata oluştu.`
            : `${response.data.created} ürün başarıyla oluşturuldu.`;

        setToasts([
          {
            description: successMessage,
            id: Date.now(),
            title: response.data.errors > 0 ? "Uyarı!" : "Başarılı!",
            appearance: response.data.errors > 0 ? "warning" : "success",
          },
        ]);

        closeSidebar();
        fetchDevices();
      }
    } catch (error) {
      console.error(
        editingDevice ? "Ürün güncelleme hatası:" : "Ürün oluşturma hatası:",
        error,
      );
      setToasts([
        {
          description:
            error.response?.data?.error ||
            error.response?.data?.message ||
            (editingDevice
              ? "Ürün güncellenirken bir hata oluştu."
              : "Ürün oluşturulurken bir hata oluştu."),
          id: Date.now(),
          title: "Hata!",
          appearance: "danger",
        },
      ]);
    } finally {
      setCreateLoading(false);
    }
  };

  return (
    <AppPage
      title={
        selectedModel ? `${selectedModel.name} - Tüm Ürünler` : "Tüm Ürünler"
      }
      breadcrumbs={
        selectedModel
          ? [...breadcrumbs, { label: selectedModel.name }]
          : breadcrumbs
      }
      actions={
        <div className="d-flex gap-3 align-items-center">
          {selectedModel && (
            <Button
              appearance="primary-subtle"
              onClick={handleBackToModels}
              icon={<FiArrowLeft />}
            >
              Modellere Dön
            </Button>
          )}
          <Button
            appearance="primary-subtle"
            icon={<IoMdBarcode />}
            onClick={() => setManualQrModalOpen(true)}
          >
            QR Kod Oluştur
          </Button>
          {!selectedModel && canWrite && (
            <Button
              appearance="primary-subtle"
              icon={<FiPlus />}
              onClick={() => {
                const now = new Date();
                setShowSingleCreateModal(true);
                setSingleDeviceData({
                  modelId: "",
                  selectedModel: null,
                  serialNumber: "",
                  month: now.getMonth() + 1,
                  year: now.getFullYear() % 100,
                  sequence: "",
                  status: "assembly",
                  notes: "",
                });
              }}
            >
              Manuel Ürün Ekle
            </Button>
          )}
          {selectedModel && canWrite && (
            <Button
              appearance="primary"
              icon={<FiPlus />}
              onClick={handleCreateModalOpen}
            >
              Yeni Ürün
            </Button>
          )}
        </div>
      }
    >
      <Container>
        <ContainerItem sm={4} md={8} xl={12}>
          {!selectedModel ? (
            <>
              <Table
                columns={modelColumns || []}
                visibleColumns={visibleModelColumns}
                tableId="products-models-list"
                loading={modelsLoading}
                search={true}
                hideFooter={true}
                viewOptions={
                  <ListViewOptionsPanel
                    columns={modelColumns}
                    visibleColumns={visibleModelColumns}
                    onToggleColumn={toggleModelColumn}
                    onResetColumns={resetModelColumns}
                    showApply={false}
                    showReset={false}
                  />
                }
              >
                {(models || []).map((model) => (
                  <Tr key={model._id} data={model}>
                    {visibleModelColumns.includes(0) && <Td>{model.name}</Td>}
                    {visibleModelColumns.includes(1) && <Td>{model.code}</Td>}
                    {visibleModelColumns.includes(2) && (
                      <Td>
                        <span
                          style={{
                            backgroundColor: "#3B82F615",
                            color: "#3B82F6",
                            padding: "5px 10px",
                            borderRadius: "5px",
                            width: "fit-content",
                            fontWeight: "600",
                          }}
                        >
                          {model.deviceCount || 0} Adet
                        </span>
                      </Td>
                    )}
                    {visibleModelColumns.includes(3) && (
                      <Td>
                        <div className="d-flex gap-2">
                          <FiEye
                            className="cursor-pointer"
                            style={{ fontSize: "20px", cursor: "pointer" }}
                            onClick={(e) => {
                              e.stopPropagation();
                              handleModelClick(model);
                            }}
                            title="Ürünleri Görüntüle"
                          />
                        </div>
                      </Td>
                    )}
                  </Tr>
                ))}
              </Table>
            </>
          ) : (
            <>
              <Table
                columns={deviceColumns || []}
                visibleColumns={visibleDeviceColumns}
                tableId="products-devices-list"
                loading={loading}
                search={true}
                externalSearch={true}
                onSearch={(value) => {
                  setSearchQuery(value);
                  setPagination((prev) => ({ ...prev, page: 1 }));
                }}
                searchPlaceholder="Seri numarası ile ara..."
                externalSorting={true}
                onSort={(nextSort) => {
                  setSort(nextSort);
                  setPagination((prev) => ({ ...prev, page: 1 }));
                }}
                hideFooter={true}
                viewOptions={
                  <ListViewOptionsPanel
                    columns={deviceColumns}
                    visibleColumns={visibleDeviceColumns}
                    onToggleColumn={toggleDeviceColumn}
                    onResetColumns={resetDeviceColumns}
                    filterSections={deviceFilterSections}
                    activeFilterCount={deviceActiveFilterCount}
                    onReset={() => {
                      setStatusFilter(null);
                      setPagination((prev) => ({ ...prev, page: 1 }));
                    }}
                    showApply={false}
                  />
                }
              >
                {(devices || []).map((device) => (
                  <Tr key={device._id} data={device}>
                    {visibleDeviceColumns.includes(1) && (
                      <Td>
                        {device.modelId?.name || "-"}
                        {device.modelId?.code && (
                          <span className="text-muted ms-1">
                            ({device.modelId.code})
                          </span>
                        )}
                      </Td>
                    )}
                    {visibleDeviceColumns.includes(0) && (
                      <Td>{device.serialNumber}</Td>
                    )}
                    {visibleDeviceColumns.includes(2) && (
                      <Td>
                        <div
                          style={{
                            backgroundColor: getStatusAppearance(device.status)
                              .bg,
                            color: getStatusAppearance(device.status).text,
                            padding: "5px 10px",
                            borderRadius: "5px",
                            width: "fit-content",
                          }}
                        >
                          {getStatusLabel(device.status)}
                        </div>
                      </Td>
                    )}
                    {visibleDeviceColumns.includes(3) && (
                      <Td>{formatInstantDateShortTr(device.createdAt)}</Td>
                    )}
                    {visibleDeviceColumns.includes(4) && (
                      <Td>
                        <div className="d-flex gap-2">
                          <IoMdBarcode
                            className="cursor-pointer text-grey"
                            style={{ fontSize: "20px", cursor: "pointer" }}
                            onClick={() => handleGenerateQR(device)}
                            title="Barkod Göster"
                          />
                          {device?.status === "delivered" && (
                            <FiTruck
                              className="cursor-pointer text-grey"
                              style={{ fontSize: "20px", cursor: "pointer" }}
                              onClick={() => handleOpenDeliveriesModal(device)}
                              title="Teslimat Detayları"
                            />
                          )}
                          {canWrite && (
                            <>
                              <FiEdit3
                                className="cursor-pointer text-grey"
                                style={{ fontSize: "20px", cursor: "pointer" }}
                                onClick={() => handleEdit(device)}
                                title="Düzenle"
                              />
                              <FiTrash2
                                className="cursor-pointer text-grey"
                                style={{ fontSize: "20px", cursor: "pointer" }}
                                onClick={() => handleDelete(device._id)}
                                title="Sil"
                              />
                            </>
                          )}
                        </div>
                      </Td>
                    )}
                  </Tr>
                ))}
              </Table>

              {/* Pagination */}
              {!loading && pagination.total > 0 && (
                <div className="mt-3">
                  <Pagination
                    totalItems={pagination.total}
                    totalPerPage={pagination.limit}
                    currentPage={pagination.page}
                    totalRecords={pagination.total}
                    onPageChange={({ pagination: paginationData }) => {
                      setPagination((prev) => ({
                        ...prev,
                        page: paginationData.currentPage,
                        limit: paginationData.itemsPerPage,
                      }));
                    }}
                    options={{
                      skipPrevious: true,
                      previous: true,
                      next: true,
                      skipNext: true,
                      selectQuantity: true,
                      simpleCounting: false,
                    }}
                  />
                </div>
              )}
            </>
          )}
        </ContainerItem>
      </Container>

      <ToastContainer>
        {toasts?.map((toast, index) => (
          <Toast
            {...toast}
            key={index}
            onDismissed={() => removeToast(index)}
            onClose={() => removeToast(index)}
          />
        ))}
      </ToastContainer>

      <Modal
        isOpen={confirmModalOpen}
        onClose={() => {
          setConfirmModalOpen(false);
          setDeviceToDelete(null);
        }}
        width="small"
      >
        <ModalHeader>Ürünü Sil</ModalHeader>
        <ModalBody>
          <p className="sub-title-page">
            Bu ürünü silmek istediğinizden emin misiniz?
          </p>
        </ModalBody>
        <ModalFooter>
          <ButtonGroup placement="end" reverse>
            <Button appearance="danger" onClick={confirmDelete}>
              Sil
            </Button>
            <Button
              appearance="subtle"
              onClick={() => {
                setConfirmModalOpen(false);
                setDeviceToDelete(null);
              }}
            >
              İptal
            </Button>
          </ButtonGroup>
        </ModalFooter>
      </Modal>

      <SidebarContainer
        show={sidebarOpened}
        title={editingDevice ? "Ürün Düzenle" : "Toplu Ürün Ekleme"}
        onClose={closeSidebar}
      >
        <div className="d-flex flex-column" style={{ height: "100%" }}>
          {editingDevice ? (
            <>
              <div className="mb-4">
                <p className="text-muted small mb-3">
                  Ürün bilgilerini düzenleyin
                </p>
                <div className="d-flex flex-column gap-3">
                  <div>
                    <Label required>Seri Numarası</Label>
                    <div
                      style={{
                        background: "#fff",
                        border: "1px solid #e2e5e8",
                        borderRadius: "6px",
                        transition: "all 0.2s ease",
                      }}
                    >
                      <Dropdown
                        placement="top-start"
                        trigger={
                          <div className="d-flex align-items-center justify-content-between p-2 w-100">
                            <span>
                              {deviceData.serialNumber || "Seri numarası seçin"}
                            </span>
                          </div>
                        }
                      >
                        <DropdownContent className="w-100">
                          {serialNumbers.length === 0 ? (
                            <DropdownItem disabled>
                              Müsait seri numarası yok
                            </DropdownItem>
                          ) : (
                            serialNumbers.map((sn) => (
                              <DropdownItem
                                key={sn._id}
                                onClick={() => {
                                  setDeviceData((prev) => ({
                                    ...prev,
                                    serialNumber: sn.serialNumber,
                                  }));
                                }}
                                active={
                                  sn.serialNumber === deviceData.serialNumber
                                }
                              >
                                {sn.serialNumber}
                              </DropdownItem>
                            ))
                          )}
                        </DropdownContent>
                      </Dropdown>
                    </div>
                  </div>
                  <div>
                    <Label required>Model</Label>
                    <div
                      style={{
                        background: "#fff",
                        border: "1px solid #e2e5e8",
                        borderRadius: "6px",
                        transition: "all 0.2s ease",
                      }}
                    >
                      <Dropdown
                        placement="top-start"
                        trigger={
                          <div className="d-flex align-items-center justify-content-between p-2 w-100">
                            <span>
                              {deviceData.selectedModel?.name || "Model seçin"}
                            </span>
                          </div>
                        }
                      >
                        <DropdownContent className="w-100">
                          {models.length === 0 ? (
                            <DropdownItem disabled>
                              Müsait model yok
                            </DropdownItem>
                          ) : (
                            models.map((model) => (
                              <DropdownItem
                                key={model._id}
                                onClick={() => handleModelSelect(model)}
                                active={
                                  model.name === deviceData.selectedModel?.name
                                }
                              >
                                {model.name} ({model.code})
                              </DropdownItem>
                            ))
                          )}
                        </DropdownContent>
                      </Dropdown>
                    </div>
                  </div>
                  <div>
                    <Label>Notlar</Label>
                    <textarea
                      style={{
                        width: "100%",
                        minHeight: "80px",
                        padding: "12px",
                        background: "#fff",
                        border: "1px solid #e2e5e8",
                        borderRadius: "6px",
                        transition: "all 0.2s ease",
                        fontSize: "14px",
                        fontFamily: "inherit",
                        resize: "vertical",
                        outline: "none",
                      }}
                      rows={3}
                      value={deviceData.notes}
                      onChange={(e) => {
                        setDeviceData((prev) => ({
                          ...prev,
                          notes: e.target.value,
                        }));
                      }}
                      placeholder="Notlar (opsiyonel)"
                      onFocus={(e) => {
                        e.target.style.borderColor =
                          "var(--m-primary, #0065ff)";
                      }}
                      onBlur={(e) => {
                        e.target.style.borderColor = "#e2e5e8";
                      }}
                    />
                  </div>
                </div>
              </div>
              <div className="mt-auto pt-3">
                <Button
                  appearance="primary"
                  onClick={handleCreateSubmit}
                  loading={createLoading}
                  icon={<FiEdit3 />}
                  className="w-100"
                >
                  Kaydet
                </Button>
              </div>
            </>
          ) : (
            <>
              <div className="mb-4">
                <p className="text-muted small mb-3">
                  Model seçin ve oluşturulacak ürün adedini girin
                </p>
                <div className="d-flex flex-column gap-3">
                  <div>
                    <Label required>Model</Label>
                    <div
                      style={{
                        background: "#fff",
                        border: "1px solid #e2e5e8",
                        borderRadius: "6px",
                        transition: "all 0.2s ease",
                      }}
                    >
                      <Dropdown
                        placement="top-start"
                        trigger={
                          <div className="d-flex align-items-center justify-content-between p-2 w-100">
                            <span>
                              {deviceData.selectedModel?.name || "Model seçin"}
                            </span>
                          </div>
                        }
                      >
                        <DropdownContent className="w-100">
                          {models.length === 0 ? (
                            <DropdownItem disabled>
                              Müsait model yok
                            </DropdownItem>
                          ) : (
                            models.map((model) => (
                              <DropdownItem
                                key={model._id}
                                onClick={() => handleModelSelect(model)}
                                active={
                                  model.name === deviceData.selectedModel?.name
                                }
                              >
                                {model.name} ({model.code})
                              </DropdownItem>
                            ))
                          )}
                        </DropdownContent>
                      </Dropdown>
                    </div>
                  </div>
                  <div>
                    <Label required>Adet</Label>
                    <Input
                      type="number"
                      min="1"
                      max="9999"
                      value={deviceData.count}
                      onChange={(value) => {
                        const num = value.replace(/[^0-9]/g, "");
                        if (
                          num === "" ||
                          (parseInt(num) >= 1 && parseInt(num) <= 9999)
                        ) {
                          setDeviceData((prev) => ({
                            ...prev,
                            count: num || "1",
                          }));
                        }
                      }}
                      placeholder="1"
                    />
                    <small className="text-muted">
                      Seri numaraları otomatik olarak oluşturulacaktır
                    </small>
                  </div>
                </div>
              </div>
              <div className="mt-auto pt-3">
                <Button
                  appearance="primary"
                  onClick={handleCreateSubmit}
                  loading={createLoading}
                  icon={<FiPlus />}
                  className="w-100"
                >
                  Oluştur
                </Button>
              </div>
            </>
          )}
        </div>
      </SidebarContainer>

      {/* Single Product Create Sidebar */}
      <SidebarContainer
        show={showSingleCreateModal}
        title="Manuel Ürün Ekle"
        onClose={() => {
          setShowSingleCreateModal(false);
          const now = new Date();
          setSingleDeviceData({
            modelId: "",
            selectedModel: null,
            serialNumber: "",
            month: now.getMonth() + 1,
            year: now.getFullYear() % 100,
            sequence: "",
            status: "assembly",
            notes: "",
          });
        }}
      >
        <div className="d-flex flex-column" style={{ height: "100%" }}>
          <div className="d-flex flex-column gap-3">
            <div>
              <Label required>Model</Label>
              <div
                style={{
                  background: "#fff",
                  border: "1px solid #e2e5e8",
                  borderRadius: "6px",
                  transition: "all 0.2s ease",
                }}
              >
                <Dropdown
                  placement="top-start"
                  trigger={
                    <div className="d-flex align-items-center justify-content-between p-2 w-100">
                      <span>
                        {singleDeviceData.selectedModel?.name || "Model seçin"}
                      </span>
                    </div>
                  }
                >
                  <DropdownContent className="w-100">
                    {models.length === 0 ? (
                      <DropdownItem disabled>Müsait model yok</DropdownItem>
                    ) : (
                      models.map((model) => (
                        <DropdownItem
                          key={model._id}
                          onClick={() => {
                            setSingleDeviceData((prev) => ({
                              ...prev,
                              selectedModel: model,
                              modelId: model._id,
                            }));
                          }}
                          active={
                            model.name === singleDeviceData.selectedModel?.name
                          }
                        >
                          {model.name} ({model.code})
                        </DropdownItem>
                      ))
                    )}
                  </DropdownContent>
                </Dropdown>
              </div>
            </div>
            <div className="d-flex gap-2">
              <div style={{ flex: 1 }}>
                <Label required>Ay</Label>
                <div
                  style={{
                    background: "#fff",
                    border: "1px solid #e2e5e8",
                    borderRadius: "6px",
                    transition: "all 0.2s ease",
                  }}
                >
                  <Dropdown
                    placement="top-start"
                    trigger={
                      <div className="d-flex align-items-center justify-content-between p-2 w-100">
                        <span>
                          {singleDeviceData.month.toString().padStart(2, "0")}
                        </span>
                      </div>
                    }
                  >
                    <DropdownContent className="w-100">
                      {Array.from({ length: 12 }, (_, i) => i + 1).map(
                        (month) => (
                          <DropdownItem
                            key={month}
                            onClick={() => {
                              setSingleDeviceData((prev) => ({
                                ...prev,
                                month: month,
                              }));
                            }}
                            active={month === singleDeviceData.month}
                          >
                            {month.toString().padStart(2, "0")}
                          </DropdownItem>
                        ),
                      )}
                    </DropdownContent>
                  </Dropdown>
                </div>
              </div>
              <div style={{ flex: 1 }}>
                <Label required>Yıl</Label>
                <div
                  style={{
                    background: "#fff",
                    border: "1px solid #e2e5e8",
                    borderRadius: "6px",
                    transition: "all 0.2s ease",
                  }}
                >
                  <Dropdown
                    placement="top-start"
                    trigger={
                      <div className="d-flex align-items-center justify-content-between p-2 w-100">
                        <span>
                          {singleDeviceData.year.toString().padStart(2, "0")}
                        </span>
                      </div>
                    }
                  >
                    <DropdownContent className="w-100">
                      {Array.from({ length: 3 }, (_, i) => {
                        const currentYear = new Date().getFullYear() % 100;
                        return (currentYear - 2 + i) % 100;
                      }).map((year) => (
                        <DropdownItem
                          key={year}
                          onClick={() => {
                            setSingleDeviceData((prev) => ({
                              ...prev,
                              year: year,
                            }));
                          }}
                          active={year === singleDeviceData.year}
                        >
                          {year.toString().padStart(2, "0")}
                        </DropdownItem>
                      ))}
                    </DropdownContent>
                  </Dropdown>
                </div>
              </div>
            </div>
            <div>
              <Label required>Sıra</Label>
              <Input
                type="number"
                min="1"
                max="9999"
                value={singleDeviceData.sequence}
                onChange={(value) => {
                  const num = value.replace(/[^0-9]/g, "");
                  if (
                    num === "" ||
                    (parseInt(num) >= 1 && parseInt(num) <= 9999)
                  ) {
                    setSingleDeviceData((prev) => ({
                      ...prev,
                      sequence: num,
                    }));
                  }
                }}
                placeholder="0001"
              />
            </div>
          </div>
          <div className="mt-auto pt-3">
            <Button
              appearance="primary"
              onClick={handleSingleCreateSubmit}
              loading={createLoading}
              icon={<FiPlus />}
              className="w-100"
            >
              Oluştur
            </Button>
          </div>
        </div>
      </SidebarContainer>

      {/* QR Barcode Modal */}
      <Modal
        isOpen={qrModalOpen}
        onClose={() => {
          setQrModalOpen(false);
          setSelectedDevice(null);
          setBarcodeValue(null);
        }}
        width="medium"
      >
        <ModalHeader>QR - {selectedDevice?.serialNumber}</ModalHeader>
        <ModalBody>
          <div style={{ textAlign: "center", padding: "20px" }}>
            {selectedDevice && (
              <>
                {barcodeValue ? (
                  <div
                    style={{
                      display: "inline-block",
                      padding: 12,
                      background: "#fff",
                    }}
                  >
                    <QRCodeCanvas value={barcodeValue} size={220} level="M" />
                  </div>
                ) : (
                  <p>QR yükleniyor...</p>
                )}
                <p style={{ marginTop: "15px", fontWeight: "bold" }}>
                  Seri No: {selectedDevice.serialNumber}
                </p>
              </>
            )}
          </div>
        </ModalBody>
        <ModalFooter>
          <ButtonGroup placement="end" reverse>
            <Button
              appearance="subtle"
              onClick={() => {
                setQrModalOpen(false);
                setSelectedDevice(null);
                setBarcodeValue(null);
              }}
            >
              Kapat
            </Button>
          </ButtonGroup>
        </ModalFooter>
      </Modal>

      <Modal
        isOpen={deliveriesModalOpen}
        onClose={() => {
          setDeliveriesModalOpen(false);
          setSelectedDeviceForDeliveries(null);
          setDeliveriesForDevice([]);
          setDeliveriesLoading(false);
        }}
        width="large"
      >
        <ModalHeader>
          Sipariş Teslim Detayları
          {selectedDeviceForDeliveries?.serialNumber && (
            <span className="text-muted small ms-2">
              - {selectedDeviceForDeliveries.serialNumber}
            </span>
          )}
        </ModalHeader>
        <ModalBody>
          {deliveriesLoading ? (
            <div className="loading-container">
              <p>Yükleniyor...</p>
            </div>
          ) : deliveriesForDevice.length > 0 ? (
            <>
              <div style={deliveryModalStyles.summary}>
                <div style={deliveryModalStyles.summaryItem}>
                  <div style={deliveryModalStyles.summaryLabel}>
                    Ürün Seri No
                  </div>
                  <div style={deliveryModalStyles.summaryValue}>
                    {selectedDeviceForDeliveries?.serialNumber || "-"}
                  </div>
                </div>
                <div style={deliveryModalStyles.summaryItem}>
                  <div style={deliveryModalStyles.summaryLabel}>
                    Teslimat Tarihi
                  </div>
                  <div style={deliveryModalStyles.summaryValue}>
                    {formatDateTime(
                      deliveriesForDevice[0]?.transferDate ||
                        deliveriesForDevice[0]?.deliveryDate ||
                        deliveriesForDevice[0]?.createdAt,
                    )}
                  </div>
                </div>
              </div>

              <div style={deliveryModalStyles.list}>
                {deliveriesForDevice.map((delivery) => (
                  <div key={delivery._id} style={deliveryModalStyles.card}>
                    <div style={deliveryModalStyles.cardTop}>
                      <div style={deliveryModalStyles.grid}>
                        <div style={deliveryModalStyles.field}>
                          <span style={deliveryModalStyles.label}>
                            Sevkiyat Adı:
                          </span>
                          <span style={deliveryModalStyles.value}>
                            {delivery?.deliveryName || "-"}
                          </span>
                        </div>
                        <div style={deliveryModalStyles.field}>
                          <span style={deliveryModalStyles.label}>Tarih:</span>
                          <span style={deliveryModalStyles.value}>
                            {formatDateTime(
                              delivery?.transferDate ||
                                delivery?.deliveryDate ||
                                delivery?.createdAt,
                            )}
                          </span>
                        </div>

                        <div style={deliveryModalStyles.field}>
                          <span style={deliveryModalStyles.label}>
                            Teslim Edilen:
                          </span>
                          <span style={deliveryModalStyles.value}>
                            {getDeliveryTargetLabel(delivery)}
                          </span>
                        </div>
                        <div style={deliveryModalStyles.field}>
                          <span style={deliveryModalStyles.label}>
                            Sipariş:
                          </span>
                          <span style={deliveryModalStyles.value}>
                            {delivery?.saleId?.saleName || "-"}
                          </span>
                        </div>

                        <div style={deliveryModalStyles.field}>
                          <span style={deliveryModalStyles.label}>
                            Teslim Alan:
                          </span>
                          <span style={deliveryModalStyles.value}>
                            {delivery?.driverName || "-"}
                          </span>
                        </div>
                        <div style={deliveryModalStyles.field}>
                          <span style={deliveryModalStyles.label}>
                            Araç Plakası:
                          </span>
                          <span style={deliveryModalStyles.value}>
                            {delivery?.vehiclePlate || "-"}
                          </span>
                        </div>

                        <div
                          style={{
                            ...deliveryModalStyles.field,
                            ...deliveryModalStyles.full,
                          }}
                        >
                          <span style={deliveryModalStyles.label}>Not:</span>
                          <span style={deliveryModalStyles.value}>
                            {delivery?.notes || "-"}
                          </span>
                        </div>
                      </div>

                      <div style={deliveryModalStyles.actions}>
                        <Button
                          appearance="primary-subtle"
                          onClick={() =>
                            navigate(`/deliveries/view/${delivery._id}`)
                          }
                          icon={<FiExternalLink />}
                        >
                          Detaylar
                        </Button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </>
          ) : (
            <div className="empty-models-state">
              <div className="empty-text">Bu ürün için teslimat bulunamadı</div>
            </div>
          )}
        </ModalBody>
        <ModalFooter>
          <ButtonGroup placement="end" reverse>
            <Button
              appearance="danger-subtle"
              onClick={() => {
                setDeliveriesModalOpen(false);
                setSelectedDeviceForDeliveries(null);
                setDeliveriesForDevice([]);
                setDeliveriesLoading(false);
              }}
            >
              Kapat
            </Button>
          </ButtonGroup>
        </ModalFooter>
      </Modal>

      {/* Manual QR Code Generation Modal */}
      <Modal
        isOpen={manualQrModalOpen}
        onClose={() => {
          setManualQrModalOpen(false);
          setManualSerialNumber("");
          setManualBarcodeValue(null);
        }}
        width="medium"
      >
        <ModalHeader>QR Kod Oluştur</ModalHeader>
        <ModalBody>
          <div className="mb-3">
            <Label>Seri Numarası</Label>
            <Input
              type="text"
              value={manualSerialNumber}
              onChange={(value) => setManualSerialNumber(value)}
              placeholder="Seri numarası girin (örn: ABC/0124/0001)"
              onKeyPress={(e) => {
                if (e.key === "Enter") {
                  handleGenerateQRFromSerial();
                }
              }}
            />
          </div>
          <div style={{ textAlign: "center", padding: "20px" }}>
            {manualBarcodeValue ? (
              <>
                <div
                  style={{
                    display: "inline-block",
                    padding: 12,
                    background: "#fff",
                  }}
                >
                  <QRCodeCanvas
                    value={manualBarcodeValue}
                    size={220}
                    level="M"
                  />
                </div>
                <p style={{ marginTop: "15px", fontWeight: "bold" }}>
                  Seri No: {manualSerialNumber}
                </p>
                <p
                  style={{
                    marginTop: "10px",
                    fontSize: "14px",
                    color: "#666",
                  }}
                >
                  Değer: {manualBarcodeValue}
                </p>
              </>
            ) : (
              <p className="text-muted">
                Seri numarası girin ve "QR Kod Oluştur" butonuna tıklayın
              </p>
            )}
          </div>
        </ModalBody>
        <ModalFooter>
          <ButtonGroup placement="end" reverse>
            <Button
              appearance="primary"
              onClick={handleGenerateQRFromSerial}
              disabled={!manualSerialNumber.trim()}
            >
              QR Kod Oluştur
            </Button>
            <Button
              appearance="subtle"
              onClick={() => {
                setManualQrModalOpen(false);
                setManualSerialNumber("");
                setManualBarcodeValue(null);
              }}
            >
              Kapat
            </Button>
          </ButtonGroup>
        </ModalFooter>
      </Modal>
    </AppPage>
  );
};

export default Devices;
